import { useEffect, useState } from "react";
import { upcomingRotationCoverage, rotationDate, rotationSpanDays, type OnCallRotation, type OnCallRotationRule } from "../../../api/src/modules/scheduling/on-call-rule";
import { apiFetch } from "../lib/api-client";
import type { Technician } from "../lib/dispatch";
import { PrimaryButton, QuietButton, Select, TextInput } from "./controls";

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
type Save = (path: string, method: "POST" | "PUT", body: unknown) => Promise<unknown>;
type OnCallHours = OnCallRotationRule["hours"];
type CoveragePreset = "after_hours_weekend" | "around_the_clock" | "custom";

// These patterns are deliberately complete. The previous defaults left a gap
// after 4 PM on Saturday, which is the opposite of what an after-hours
// schedule is expected to communicate.
const afterHoursAndWeekend: OnCallHours = [
  { day: 0, start: "08:00", end: "08:00" },
  { day: 1, start: "16:00", end: "08:00" },
  { day: 2, start: "16:00", end: "08:00" },
  { day: 3, start: "16:00", end: "08:00" },
  { day: 4, start: "16:00", end: "08:00" },
  { day: 5, start: "16:00", end: "08:00" },
  { day: 6, start: "08:00", end: "08:00" },
];
const aroundTheClock: OnCallHours = days.map((_, day) => ({ day, start: "00:00", end: "00:00" }));

function copyHours(hours: OnCallHours) { return hours.map(hour => ({ ...hour })); }
function hasHours(hours: OnCallHours, expected: OnCallHours) {
  return hours.length === expected.length && expected.every((hour, index) =>
    hours[index]?.day === hour.day && hours[index]?.start === hour.start && hours[index]?.end === hour.end);
}
function coveragePreset(hours: OnCallHours): CoveragePreset {
  if (hasHours(hours, afterHoursAndWeekend)) return "after_hours_weekend";
  if (hasHours(hours, aroundTheClock)) return "around_the_clock";
  return "custom";
}

export function newRotationRule(date: string, technicianId: string): OnCallRotationRule {
  return {
    name: "On call", technicianId, startDate: date, endDate: null, coverageEndDate: rotationDate(date, 6), repeats: "weekly", repeatEvery: 1,
    hours: copyHours(afterHoursAndWeekend),
  };
}

export function OnCallRotationEditor({ technicians, date, timezone, initial, technicianId = "", save, done }: {
  technicians: Technician[]; date: string; timezone: string; initial?: OnCallRotation;
  technicianId?: string; save: Save; done?: () => void;
}) {
  const [rule, setRule] = useState<OnCallRotationRule>(() => initial
    ? { name: initial.name, technicianId: initial.technicianId, startDate: initial.startDate, endDate: initial.endDate, coverageEndDate: initial.coverageEndDate, repeats: initial.repeats, repeatEvery: initial.repeatEvery, hours: initial.hours }
    : newRotationRule(date, technicianId));
  const [id] = useState(() => initial?.id ?? crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preset, setPreset] = useState<CoveragePreset>(() => coveragePreset(initial?.hours ?? afterHoursAndWeekend));
  const zone = initial?.timezone ?? timezone;
  const update = (patch: Partial<OnCallRotationRule>) => setRule(current => ({ ...current, ...patch }));
  const ready = !!(rule.technicianId && rule.startDate && rule.coverageEndDate && rotationSpanDays(rule) >= 1 && rotationSpanDays(rule) <= 84 && Number.isInteger(rule.repeatEvery) && rule.repeatEvery >= 1 && rule.repeatEvery <= (rule.repeats === "weekly" ? 52 : 366) && rule.hours.length && rule.hours.every(hours => hours.start && hours.end) && (rule.repeats === "never" || !rule.endDate || rule.endDate >= rule.coverageEndDate));
  const name = technicians.find(tech => tech.id === rule.technicianId)?.name ?? "Technician";
  const previewDate = date > rule.startDate ? date : rule.startDate;
  const preview = ready ? upcomingRotationCoverage(
    { ...rule, id, timezone: zone, revision: initial?.revision ?? 1, stoppedAt: null }, previewDate,
  ) : [];
  const format = (value: string) => new Intl.DateTimeFormat(undefined, {
    timeZone: zone, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  }).format(new Date(value));

  return <form className="space-y-4" onSubmit={async event => {
    event.preventDefault();
    if (busy || !ready) return;
    setBusy(true); setError("");
    try {
      await save(initial ? `/api/v1/workspace/dispatch/on-call-rotations/${id}` : "/api/v1/workspace/dispatch/on-call-rotations",
        initial ? "PUT" : "POST", initial ? { revision: initial.revision, rule: { ...rule, endDate: rule.repeats === "never" ? null : rule.endDate } } : { id, rule: { ...rule, endDate: rule.repeats === "never" ? null : rule.endDate } });
      done?.();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "The on-call schedule could not be saved."); }
    finally { setBusy(false); }
  }}>
    <fieldset disabled={busy} className="space-y-4">
      {technicianId || initial ? <p className="font-semibold text-[var(--ink)]">On-call availability for {name}</p>
        : <Select label="Technician" required value={rule.technicianId} options={technicians.map(tech => ({ value: tech.id, label: tech.name }))} onChange={technicianId => update({ technicianId })} />}
      <p className="text-sm text-[var(--ink-soft)]">Choose this technician’s first coverage dates and how often that range repeats. Times use {zone}.</p>
      <TextInput label="Label" required value={rule.name} maxLength={120} onChange={event => update({ name: event.target.value })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <TextInput label="First rotation starts" required type="date" value={rule.startDate} onChange={event => {
          const startDate = event.target.value;
          const days = Number.isFinite(rotationSpanDays(rule)) ? rotationSpanDays(rule) : 7;
          update({ startDate, coverageEndDate: startDate ? rotationDate(startDate, Math.max(0, days - 1)) : "" });
        }} />
        <TextInput label="First rotation through" required type="date" min={rule.startDate} value={rule.coverageEndDate} onChange={event => update({ coverageEndDate: event.target.value })} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Select label="Repeats" value={rule.repeats} options={[{ value: "never", label: "Never" }, { value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" }]} onChange={value => update({ repeats: value as OnCallRotationRule["repeats"], repeatEvery: 1, endDate: value === "never" ? null : rule.endDate })} />
        {rule.repeats !== "never" && <TextInput label={rule.repeats === "weekly" ? "Repeat every (weeks)" : "Repeat every (days)"} required type="number" min={1} max={rule.repeats === "weekly" ? 52 : 366} value={rule.repeatEvery || ""} onChange={event => update({ repeatEvery: Number(event.target.value) })} />}
      </div>
      <p className="text-xs text-[var(--ink-soft)]">{rule.repeats === "never" ? "Covers only the first date range." : `The ${rotationSpanDays(rule) || "…"}-day coverage range repeats every ${rule.repeatEvery || "…"} ${rule.repeats === "weekly" ? "week(s)" : "day(s)"}, starting from ${rule.startDate}.`} Every date in a coverage range is included, including Sunday.</p>
      <fieldset className="space-y-2">
        <legend className="mb-2 font-semibold text-sm">On-call coverage</legend>
        <Select label="Coverage pattern" value={preset} options={[
          { value: "after_hours_weekend", label: "After-hours + full weekend", note: "Weekdays 4 PM–8 AM; Friday 4 PM through Monday 8 AM." },
          { value: "around_the_clock", label: "24/7 coverage", note: "The selected technician is available all day, every day." },
          { value: "custom", label: "Custom hours", note: "Set a separate start and end time for each day." },
        ]} onChange={value => {
          const next = value as CoveragePreset;
          setPreset(next);
          if (next === "after_hours_weekend") update({ hours: copyHours(afterHoursAndWeekend) });
          if (next === "around_the_clock") update({ hours: copyHours(aroundTheClock) });
        }} />
        {preset === "after_hours_weekend" && <p className="text-xs text-[var(--ink-soft)]">This covers weekday evenings and nights, then continuously from Friday at 4 PM until Monday at 8 AM.</p>}
        {preset === "around_the_clock" && <p className="text-xs text-[var(--ink-soft)]">This technician can be booked at any time during each covered date.</p>}
        {preset === "custom" && <>
          <p className="text-xs text-[var(--ink-soft)]">An earlier end time means the next day; matching times mean 24 hours.</p>
          {days.map((day, index) => {
          const hours = rule.hours.find(hours => hours.day === index)!;
          return <div key={day} className="grid grid-cols-[45px_1fr_1fr] gap-2 items-center border-b border-[var(--line)] py-2">
            <span className="text-sm">{day.slice(0,3)}</span>
            <TextInput label={`${day} start`} type="time" required value={hours.start} onChange={event => { setPreset("custom"); update({ hours: rule.hours.map(value => value.day === index ? { ...value, start: event.target.value } : value) }); }} />
            <TextInput label={`${day} end`} type="time" required value={hours.end} onChange={event => { setPreset("custom"); update({ hours: rule.hours.map(value => value.day === index ? { ...value, end: event.target.value } : value) }); }} />
          </div>;
          })}
        </>}
      </fieldset>
      {rule.repeats !== "never" && <TextInput label="Stop repeating on (optional)" type="date" min={rule.coverageEndDate} value={rule.endDate ?? ""} hint="Leave blank to keep repeating. Overnight coverage starting on this date finishes the next morning." onChange={event => update({ endDate: event.target.value || null })} />}
      {!!preview.length && <details className="text-sm">
        <summary className="cursor-pointer font-semibold">Upcoming on-call dates for {name}</summary>
        <ul className="mt-2 space-y-2" aria-label="Upcoming on-call coverage">{preview.map(window => <li key={window.id}>{format(window.startsAt)} → {format(window.endsAt)}</li>)}</ul>
      </details>}
      {initial && <p className="text-xs text-[var(--ink-soft)]">Saving updates this recurring schedule. Existing booked appointments stay assigned.</p>}
      {error && <p role="alert" className="text-sm text-[var(--accent)]">{error}</p>}
      <PrimaryButton disabled={busy || !ready}>{busy ? "Saving schedule…" : "Save on-call schedule"}</PrimaryButton>
    </fieldset>
  </form>;
}

export function OnCallRotations({ technicians, technicianId, date, timezone, save }: {
  technicians: Technician[]; technicianId: string; date: string; timezone: string; save: Save;
}) {
  const [items, setItems] = useState<OnCallRotation[]>([]);
  const [selected, setSelected] = useState<OnCallRotation | "new" | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let current = true;
    setLoading(true); setError("");
    void apiFetch(`/api/v1/workspace/dispatch/on-call-rotations?technicianId=${encodeURIComponent(technicianId)}`).then(async response => {
      if (!response.ok) throw new Error("Could not load this technician’s on-call schedules. Please try again.");
      const body = await response.json() as { data: { items: OnCallRotation[] } };
      if (current) setItems(body.data.items.filter(item => item.technicianId === technicianId));
    }).catch(cause => { if (current) setError(cause instanceof Error ? cause.message : "Could not load schedules."); }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [version, technicianId]);
  const done = () => { setSelected(null); setVersion(value => value + 1); };
  if (selected) return <div className="space-y-4">
    <QuietButton type="button" onClick={() => setSelected(null)}>Back to schedules</QuietButton>
    <OnCallRotationEditor key={selected === "new" ? "new" : selected.id} initial={selected === "new" ? undefined : selected} technicianId={technicianId} technicians={technicians} date={date} timezone={timezone} save={save} done={done} />
  </div>;
  return <div className="space-y-4">
    <p className="text-sm text-[var(--ink-soft)]">Recurring on-call availability for {technicians.find(tech => tech.id === technicianId)?.name ?? "this technician"}.</p>
    {loading && <p role="status">Loading schedules…</p>}
    {error && <p role="alert">{error} <QuietButton type="button" onClick={() => setVersion(value => value+1)}>Retry</QuietButton></p>}
    {!loading && !error && !items.length && <p className="text-sm">No recurring on-call schedule yet.</p>}
    {items.map(item => <div key={item.id} className="rounded-lg border border-[var(--line)] p-3 space-y-2">
      <p className="font-semibold">{item.name}{item.stoppedAt ? " · Stopped" : ""}</p>
      <p className="text-sm">{item.startDate} through {item.coverageEndDate} · {item.repeats === "never" ? "Does not repeat" : `Every ${item.repeatEvery} ${item.repeats === "weekly" ? "week(s)" : "day(s)"}`}{item.repeats !== "never" ? item.endDate ? ` · Repeats until ${item.endDate}` : " · No repeat end date" : ""}</p>
      {!item.stoppedAt && <div className="flex gap-3">
        <QuietButton type="button" disabled={busy} onClick={() => setSelected(item)}>View / edit schedule</QuietButton>
        <QuietButton type="button" disabled={busy} onClick={async () => {
          setBusy(true); setError("");
          try { await save(`/api/v1/workspace/dispatch/on-call-rotations/${item.id}/stop`, "POST", { revision: item.revision }); done(); }
          catch (cause) { setError(cause instanceof Error ? cause.message : "Could not stop schedule."); }
          finally { setBusy(false); }
        }}>Stop future coverage</QuietButton>
      </div>}
    </div>)}
    <QuietButton type="button" disabled={loading || busy} onClick={() => setSelected("new")}>Add recurring on-call schedule</QuietButton>
  </div>;
}
